import { useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';

import type { ProgressStep, ProgressStepSelection } from '../../cdl/ProgressSteps/ProgressSteps';
import { translate } from '../../common/helpers/translate.helper';
import { isDemandDefinitionSubstep } from '../isDemandDefinitionSubstep';
import { DemandDefinitionStep } from '../types/DemandDefinitionStep';
import { DEMAND_DEFINITION_SUBSTEPS, DemandDefinitionSubstep } from '../types/DemandDefinitionSubstep';

interface UseDemandDefinitionProgressParams {
    definitionId?: string;
    hasReferencePeriod: boolean;
    hasRequiredCustomerGroup: boolean;
    substep: string;
}

const STEP_BY_SUBSTEP: Record<DemandDefinitionSubstep, DemandDefinitionStep> = {
    [DemandDefinitionSubstep.GENERAL]: DemandDefinitionStep.DEFINE_BASELINE,
    [DemandDefinitionSubstep.REFERENCE_PERIOD]: DemandDefinitionStep.DEFINE_BASELINE,
    [DemandDefinitionSubstep.FLEET_SCOPE]: DemandDefinitionStep.DEFINE_BASELINE,
    [DemandDefinitionSubstep.PRODUCT_CATEGORIES]: DemandDefinitionStep.DEFINE_BASELINE,
    [DemandDefinitionSubstep.HISTORICAL_DEMAND]: DemandDefinitionStep.DEFINE_BASELINE,
    [DemandDefinitionSubstep.NEWLY_ADDED_VESSELS]: DemandDefinitionStep.REVIEW_BASELINE,
    [DemandDefinitionSubstep.DEACTIVATED_VESSELS]: DemandDefinitionStep.REVIEW_BASELINE,
    [DemandDefinitionSubstep.FLEET_CHANGES]: DemandDefinitionStep.FORECAST_DEMAND,
    [DemandDefinitionSubstep.PRODUCT_CHANGES]: DemandDefinitionStep.FORECAST_DEMAND,
    [DemandDefinitionSubstep.DEMAND_ADJUSTMENTS]: DemandDefinitionStep.FORECAST_DEMAND,
    [DemandDefinitionSubstep.TOTAL_VOLUMES]: DemandDefinitionStep.FINALIZE_TENDER_DEMAND,
    [DemandDefinitionSubstep.PORT_ALLOCATION]: DemandDefinitionStep.FINALIZE_TENDER_DEMAND,
    [DemandDefinitionSubstep.SUMMARY]: DemandDefinitionStep.REVIEW_AND_EXPORT,
};

const STEP_ORDER = [
    DemandDefinitionStep.DEFINE_BASELINE,
    DemandDefinitionStep.REVIEW_BASELINE,
    DemandDefinitionStep.FORECAST_DEMAND,
    DemandDefinitionStep.FINALIZE_TENDER_DEMAND,
    DemandDefinitionStep.REVIEW_AND_EXPORT,
];

export const useDemandDefinitionProgress = ({
    definitionId,
    hasReferencePeriod,
    hasRequiredCustomerGroup,
    substep,
}: UseDemandDefinitionProgressParams) => {
    const navigate = useNavigate();
    const activeSubstep = isDemandDefinitionSubstep(substep) ? substep : DemandDefinitionSubstep.GENERAL;

    useEffect(() => {
        if (activeSubstep !== substep) {
            if (!definitionId) {
                navigate({
                    to: '/demand-definitions/create/$substep',
                    params: { substep: activeSubstep },
                    search: { customerGroupId: undefined },
                    replace: true,
                });
                return;
            }

            navigate({
                to: '/demand-definitions/edit/$definitionId/$substep',
                params: { definitionId, substep: activeSubstep },
                search: { customerGroupId: undefined },
                replace: true,
            });
        }
    }, [activeSubstep, definitionId, navigate, substep]);

    const navigateToSubstep = (nextSubstep: DemandDefinitionSubstep) => {
        if (!definitionId) {
            navigate({
                to: '/demand-definitions/create/$substep',
                params: { substep: nextSubstep },
                search: { customerGroupId: undefined },
            });
            return;
        }

        navigate({
            to: '/demand-definitions/edit/$definitionId/$substep',
            params: { definitionId, substep: nextSubstep },
            search: { customerGroupId: undefined },
        });
    };

    const navigateToDefinition = (nextDefinitionId: string, nextSubstep: DemandDefinitionSubstep) => {
        navigate({
            to: '/demand-definitions/edit/$definitionId/$substep',
            params: { definitionId: nextDefinitionId, substep: nextSubstep },
            search: { customerGroupId: undefined },
            replace: true,
        });
    };

    const activeStepIndex = STEP_ORDER.indexOf(STEP_BY_SUBSTEP[activeSubstep]);
    const defineBaselineStep: ProgressStep<DemandDefinitionStep, DemandDefinitionSubstep> = {
        id: DemandDefinitionStep.DEFINE_BASELINE,
        label: translate('demandDefinition.defineBaseline'),
        completed: activeStepIndex > 0,
        substeps: [
            {
                id: DemandDefinitionSubstep.GENERAL,
                label: translate('demandDefinition.details'),
            },
            {
                id: DemandDefinitionSubstep.REFERENCE_PERIOD,
                label: translate('demandDefinition.referencePeriod'),
            },
            {
                id: DemandDefinitionSubstep.FLEET_SCOPE,
                label: translate('demandDefinition.fleetScope'),
            },
            {
                id: DemandDefinitionSubstep.PRODUCT_CATEGORIES,
                label: translate('demandDefinition.productCategories'),
            },
            {
                id: DemandDefinitionSubstep.HISTORICAL_DEMAND,
                label: translate('demandDefinition.historicalDemand'),
            },
        ],
    };
    const reviewBaselineStep: ProgressStep<DemandDefinitionStep, DemandDefinitionSubstep> = {
        id: DemandDefinitionStep.REVIEW_BASELINE,
        label: translate('demandDefinition.reviewBaseline'),
        completed: activeStepIndex > 1,
        disabled: !hasRequiredCustomerGroup || !hasReferencePeriod,
        substeps: [
            {
                id: DemandDefinitionSubstep.NEWLY_ADDED_VESSELS,
                label: translate('demandDefinition.addedVessels'),
            },
            {
                id: DemandDefinitionSubstep.DEACTIVATED_VESSELS,
                label: translate('demandDefinition.deactivatedVessels'),
            },
        ],
    };
    const forecastDemandStep: ProgressStep<DemandDefinitionStep, DemandDefinitionSubstep> = {
        id: DemandDefinitionStep.FORECAST_DEMAND,
        label: translate('demandDefinition.forecastDemand'),
        completed: activeStepIndex > 2,
        disabled: !hasRequiredCustomerGroup || !hasReferencePeriod,
        substeps: [
            { id: DemandDefinitionSubstep.FLEET_CHANGES, label: translate('demandDefinition.fleetChanges') },
            { id: DemandDefinitionSubstep.PRODUCT_CHANGES, label: translate('demandDefinition.productChanges') },
            { id: DemandDefinitionSubstep.DEMAND_ADJUSTMENTS, label: translate('demandDefinition.demandAdjustments') },
        ],
    };
    const finalizeTenderDemandStep: ProgressStep<DemandDefinitionStep, DemandDefinitionSubstep> = {
        id: DemandDefinitionStep.FINALIZE_TENDER_DEMAND,
        label: translate('demandDefinition.finalizeTenderDemand'),
        completed: activeStepIndex > 3,
        disabled: !hasRequiredCustomerGroup || !hasReferencePeriod,
        substeps: [
            { id: DemandDefinitionSubstep.TOTAL_VOLUMES, label: translate('demandDefinition.totalVolumes') },
            { id: DemandDefinitionSubstep.PORT_ALLOCATION, label: translate('demandDefinition.portAllocation') },
        ],
    };
    const reviewAndExportStep: ProgressStep<DemandDefinitionStep, DemandDefinitionSubstep> = {
        id: DemandDefinitionStep.REVIEW_AND_EXPORT,
        label: translate('demandDefinition.reviewAndExport'),
        disabled: !hasRequiredCustomerGroup || !hasReferencePeriod,
        substeps: [{ id: DemandDefinitionSubstep.SUMMARY, label: translate('demandDefinition.summary') }],
    };
    const progressSteps: ProgressStep<DemandDefinitionStep, DemandDefinitionSubstep>[] = [
        defineBaselineStep,
        reviewBaselineStep,
        forecastDemandStep,
        finalizeTenderDemandStep,
        reviewAndExportStep,
    ];
    const availableSubsteps = DEMAND_DEFINITION_SUBSTEPS;
    const activeSubstepIndex = availableSubsteps.indexOf(activeSubstep);
    const progressSelection: ProgressStepSelection<DemandDefinitionStep, DemandDefinitionSubstep> = {
        stepId: STEP_BY_SUBSTEP[activeSubstep],
        substepId: activeSubstep,
    };

    return {
        activeSubstep,
        navigateToDefinition,
        navigateToSubstep,
        nextSubstep: availableSubsteps[activeSubstepIndex + 1],
        progressSelection,
        progressSteps,
    };
};
